<script setup lang="ts">
import UPane from '@d/app/components/layout/UPane.vue'
import { fetchManuals, manual } from '@d/app/helper/manual'
import { setTitle } from '@d/app/helper/pages'
import { VERSIONING_FOLDER } from '@d/cache/versioning'
import { mdiBookOpenVariant, mdiBookshelf, mdiBookOpenPageVariant, mdiBookAlert, mdiArrowLeftCircle, mdiFolderCancelOutline, mdiFolderOpen, mdiFolder, mdiLockAlert } from '@mdi/js'
import 'splitpanes/dist/splitpanes.css'
import '../style/main.scss'

const route = useRoute('manual-shared-tenant-key-doc')
const router = useRouter()
const isPrintView = computed(() => (route.name as unknown) === 'manual-shared-tenant-key-print')
const loading = ref(true)

const selected = ref<string[]>([])

const lastSelected = ref<string>('')

const { t } = useI18n({ useScope: 'local' })

interface DocTreeItem {
  path: string
  treePath: string
  title: string
  hasIndex: boolean
  isRoot: boolean
  children: DocTreeItem[] | undefined
}

const tree = ref<DocTreeItem>({ path: '', treePath: '', title: '', hasIndex: false, isRoot: true, children: undefined })

function insertIntoTree(it: DocTreeItem, pre: string, post: string, title: string) {
  const folderStack: { prefix: string, postfix: string, item: DocTreeItem }[] = [{ prefix: pre, postfix: post, item: it }]

  while (folderStack.length > 0) {
    const element = folderStack.pop()!
    const prefixpath = element.prefix
    const postfixpath = element.postfix
    const item = element.item
    const index = postfixpath.indexOf('/', 1)
    if (index !== -1) { // there are more children
      const prefix = prefixpath + postfixpath.substring(0, index)
      const postfix = postfixpath.substring(index)
      const folderTitle = prefix.substring(prefix.lastIndexOf('/') + 1)
      let folder = item.children!.find(e => e.treePath === prefix)
      if (!folder) {
        folder = { path: prefix, treePath: prefix, title: folderTitle, hasIndex: false, isRoot: false, children: [] }
        item.children!.push(folder)
      }
      // insertIntoTree(folder, prefix, postfix, title)
      folderStack.push({ prefix, postfix, item: folder })
    }
    else {
      if (postfixpath === '/index.md') {
        item.hasIndex = true
        item.path = `${item.path}/index.md`
      }
      else {
        const fullpath = prefixpath + postfixpath
        item.children!.push({ path: fullpath, treePath: fullpath, title: title.substring(title.lastIndexOf('/') + 1), hasIndex: false, isRoot: false, children: undefined })
      }
    }
  }
}

function buildDocsTree() {
  const tree: DocTreeItem = {
    path: '/',
    treePath: '/',
    title: manual.value.title ?? '',
    hasIndex: false,
    isRoot: true,
    children: [],
  }

  for (const doc of manual.value.docs) {
    const index = doc.path.indexOf('/', 1)
    if (index === -1) {
      if (doc.path === '/index.md') {
        tree.hasIndex = true
        tree.path = `${tree.path}index.md`
      }
      else {
        tree.children!.push({ path: doc.path, treePath: doc.path, title: doc.title, hasIndex: false, isRoot: false, children: undefined })
      }
    }
    else {
      const prefix = doc.path.substring(0, index)
      const postfix = doc.path.substring(index)
      const folderTitle = prefix.substring(prefix.lastIndexOf('/') + 1)
      let item = tree.children!.find(e => e.treePath === prefix)
      if (!item) {
        item = { path: prefix, treePath: prefix, title: folderTitle, hasIndex: false, isRoot: false, children: [] }
        tree.children!.push(item)
      }
      insertIntoTree(item, prefix, postfix, doc.title)
    }
  }

  return tree
}

function findInTree(treeItem: DocTreeItem, path: string): DocTreeItem | undefined {
  const folderStack: DocTreeItem[] = [treeItem]
  while (folderStack.length > 0) {
    const item = folderStack.pop()!
    if (item.path === path)
      return item
    if (item.children) {
      folderStack.push(...item.children)
    }
  }
  return undefined
}

function getDocumentTitle(title: string): string {
  return title.replace('/', ' -> ')
}

const selectedDocument = computed(() => {
  return manual.value.docs.find(m => m.path === selected.value[0])
})

async function select(path: string) {
  const treeItem = findInTree(tree.value!, path)
  if (!treeItem) {
    selected.value = [lastSelected.value]
    return
  }
  else {
    if (treeItem.children && !treeItem.hasIndex) {
      selected.value = [lastSelected.value]
      return
    }
  }
  selected.value = [path]
  lastSelected.value = path
  setTitle(`${t('manualTitle')} | ${getDocumentTitle(selectedDocument.value?.title ?? '')}`)
  if (path.length > 0) {
    await router.push({ name: 'manual-shared-tenant-key-doc', params: { tenant: route.params.tenant, key: route.params.key, doc: [path.slice(1)] } })
  }
  else {
    await router.push({ name: 'manual-shared-tenant-key', params: { tenant: route.params.tenant, key: route.params.key } })
  }
}

const filteredDocs = computed(() => {
  return manual.value.docs.filter(doc => !['binfolder.bin', VERSIONING_FOLDER].includes(doc.path.slice(1)))
})

const opened = ref<string[]>(['/'])

const loginRequired = ref(false)
const passwordInput = ref('')

async function loadManual(password?: string): Promise<void> {
  try {
    const { documents, paths, title } = await fetchManuals(route.params.key, password)
    manual.value.docs = documents
    manual.value.title = title

    tree.value = buildDocsTree()

    selected.value = [`/${route.params.doc}`]
    if (!paths.includes(selected.value[0])) {
      await select(documents[0].path)
    }

    opened.value = [selected.value[0]]
    lastSelected.value = selected.value[0]
    setTitle(`${t('manualTitle')} | ${getDocumentTitle(selectedDocument.value?.title ?? '')}`)
  }
  catch (e: any) {
    if (e.response.status === 401) {
      loginRequired.value = true
      setTitle(t('passwordRequiredTitle'))
    }
  }
  finally {
    loading.value = false
  }
}

onMounted(async () => {
  const password = window.sessionStorage.getItem('manualpassword') ?? undefined
  await loadManual(password)
})

async function setPassword(): Promise<void> {
  const crypto = window.crypto.subtle
  const encoder = new TextEncoder()
  const data = encoder.encode(passwordInput.value)
  const hash = await crypto.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hash))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  window.sessionStorage.setItem('manualpassword', hashHex)
  loading.value = true
  loginRequired.value = false
  await loadManual(hashHex)
  passwordInput.value = ''
}

function backToManualView() {
  select(selected.value[0])
}
</script>

<template>
  <UPane :show-toggle="filteredDocs && filteredDocs.length > 0" :show-left="filteredDocs && filteredDocs.length > 0" :title="t('manualTitle')">
    <template #left>
      <template v-if="!isPrintView">
        <v-treeview
          :items="[tree]"
          :opened="opened"
          :activated="selected"
          color="primary"
          item-value="path"
          activatable
          @update:activated="(v) => select((v as string[])[0])"
        >
          <template #prepend="{ item, isOpen }">
            <v-icon v-if="item.isRoot" :icon="mdiBookshelf" />
            <template v-else-if="item.children && !item.hasIndex">
              <v-icon v-if="isOpen" :icon="mdiFolderOpen" />
              <v-icon v-else :icon="mdiFolder" />
            </template>
            <template v-else>
              <v-icon v-if="item.path === selected[0]" :icon="mdiBookOpenPageVariant" />
              <v-icon v-else :icon="mdiBookOpenVariant" />
            </template>
          </template>
        </v-treeview>
      </template>
      <template v-else>
        <v-list-item class="back">
          <div style="font-size: 1.5rem; display: flex; justify-content: center;">
            Handbuch exportieren
          </div>
        </v-list-item>
        <v-list-item class="back" @click="backToManualView">
          <template #prepend>
            <v-icon left color="primary" size="24">
              {{ mdiArrowLeftCircle }}
            </v-icon>
          </template>
          <template #title>
            <span>Zurück zur Übersicht</span>
          </template>
        </v-list-item>
      </template>
    </template>
    <template #right>
      <Suspense>
        <slot v-if="filteredDocs && filteredDocs.length > 0 && !loading" hallo="hey" />
        <v-container v-else-if="!loading && loginRequired" class="text-center pa-10" style="height: 100%; display: flex; flex-direction: column; justify-content: center;">
          <v-card class="elevation-2">
            <v-card-title class="headline font-weight-bold text-center mb-4">
              {{ t('passwordRequiredTitle') }}
            </v-card-title>
            <v-card-text class="text-center">
              <v-icon large color="error" size="64" :icon="mdiLockAlert" />
              <p class="mt-4 grey--text text--darken-1">
                {{ t('passwordRequiredText') }}
              </p>
              <p>
                <v-text-field v-model="passwordInput" @keydown.enter.prevent="setPassword" />
              </p>
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" :disabled="passwordInput.length === 0" @click="setPassword">
                {{ t('unlock') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-container>
        <v-container v-else-if="!loading" class="text-center pa-10" style="height: 100%; display: flex; flex-direction: column; justify-content: center;">
          <v-card class="elevation-2">
            <v-card-title class="headline font-weight-bold text-center mb-4">
              {{ manual.title ? t('noContent.title') : t('noManual.title') }}
            </v-card-title>
            <v-card-text class="text-center">
              <v-icon large color="error" size="64" :icon="manual.title ? mdiFolderCancelOutline : mdiBookAlert" />
              <p class="mt-4 grey--text text--darken-1">
                {{ manual.title ? t('noContent.desc') : t('noManual.desc') }}
              </p>
            </v-card-text>
          </v-card>
        </v-container>
        <template #fallback>
          <v-skeleton-loader type="article" />
        </template>
      </Suspense>
    </template>
  </UPane>
</template>

<style scoped>
.link{
  text-transform: none !important;
}

.back{
  border-bottom: 1px solid rgba(var(--v-theme-primary),0.3);
  padding-top: 12px;
  padding-bottom: 12px;
}
</style>

<i18n lang="yaml">
  de:
    noManual:
      title: Kein Handbuch gefunden
      desc: Zu diesem Access-Key konnte kein Handbuch gefunden werden
    noContent:
      title: Keine Dokumente gefunden
      desc: Dieses Handbuch enthält keine Dokumente
    manualTitle: Handbuch
    passwordRequiredTitle: Passwortgeschütztes Handbuch
    passwordRequiredText: Bitte das Passwort eingeben um das Handbuch in diesem Browsertab zu entsperren
    unlock: Entsperren
  en:
    noManual:
      title: No Manual Found
      desc: No Manual could be found for this access key
    noContent:
      title: No Documents Found
      desc: This Manual contains no Documents
    manualTitle: Manual
    passwordRequiredTitle: Password-protected manual
    passwordRequiredText: Please enter the password to unlock the manual for this browsertab
    unlock: Unlock
</i18n>
